/**
 * useSignedPdfHref — Hook de pré-fetch d'URL signée PDF pour iPhone Safari.
 *
 * BLOC 2026-10-18 20h55 — Refonte complète (anti-`about:blank`).
 * -------------------------------------------------------------
 * Historique du bug :
 *   R2 : window.open post-await → bloqué user gesture iPhone (silencieux).
 *   R3 : popup pré-ouverte sync + popup.location post-await → `noopener`
 *   détache la ref iOS → mutation `.location.href` silencieusement ignorée
 *   → onglet reste `about:blank`. Message "Autorisez les popups" trompeur.
 *
 * Approche définitive : AUCUN `window.open`, AUCUN `location.href` JS.
 * Le lien signé est pré-fetché en amont (ouverture menu ou mount page),
 * puis rendu via `<a href={signedUrl} target="_blank" rel="noopener noreferrer">`
 * → navigation native du navigateur (user gesture préservé, pas de ref
 * à muter, iPhone Safari ouvre l'URL directement).
 *
 * API :
 *   const { href, loading, error, expiresAt, refresh } = useSignedPdfHref(
 *     "invoice", invoice.id, { enabled: menuOpen }
 *   );
 *   // href = string | null ; loading = bool ; error = string | null
 *
 * TTL backend : 3 min. Refresh auto 10s avant expiration si l'utilisateur
 * reste actif. Détection expiration passive via `document.visibilitychange`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const REFRESH_MARGIN_MS = 10_000; // refresh 10s avant expiration

export function useSignedPdfHref(entityType, entityId, { enabled = true } = {}) {
  const [href, setHref] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const abortRef = useRef(null);
  const lastKeyRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!entityType || !entityId) {
      setHref(null);
      setError(null);
      setExpiresAt(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(
        "/pdf/sign",
        { document_type: entityType, document_id: entityId },
        { signal: ctrl.signal },
      );
      // Backend renvoie `/api/pdf/signed/{token}` → absolu via REACT_APP_BACKEND_URL
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absolute = `${base}${data.url}`;
      setHref(absolute);
      const expMs = Date.now() + (data.expires_in || 180) * 1000;
      setExpiresAt(expMs);
    } catch (e) {
      if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
      setError(e?.response?.data?.detail || "PDF indisponible");
      setHref(null);
      setExpiresAt(null);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  // Fetch au mount/enable change
  useEffect(() => {
    if (!enabled) return;
    const key = `${entityType}|${entityId}`;
    if (key === lastKeyRef.current && href && expiresAt && expiresAt > Date.now() + REFRESH_MARGIN_MS) {
      return; // encore valide
    }
    lastKeyRef.current = key;
    refresh();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityType, entityId]);

  // Refresh si retour sur l'onglet et lien expiré/proche expir
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!expiresAt || expiresAt < Date.now() + REFRESH_MARGIN_MS) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, expiresAt, refresh]);

  return { href, loading, error, expiresAt, refresh };
}
